import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone } from 'lucide-react';
import PageHero from '../../components/PageHero';
import './ContactPage.css';
import contactHeroImg from '../../Assets/Image/Contact Us.webp';
import SEO from '../../components/SEO';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const ContactPage = () => {
  const [content, setContent] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/contact`);
        setContent(res.data || null);
      } catch (err) {
        setContent({});
      }
    };
    fetchContact();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const heroTitle = content?.heroTitle ?? 'Contact Us';
  const heroImageFromApi = content?.heroImage;
  const heroImage = heroImageFromApi
    ? (heroImageFromApi.startsWith('http') ? heroImageFromApi : `${API_BASE_URL}${heroImageFromApi.startsWith('/') ? '' : '/'}${heroImageFromApi}`)
    : contactHeroImg;
  const formHeading = content?.formHeading ?? 'Leave us your info';
  const location = content?.location ?? 'Sheikh Zaid, Giza, Egypt';
  const email = content?.email ?? 'Info@WhalesAdvisory.com';
  const phone = content?.phone ?? '01000355950';

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us"
        description="Contact Whales Advisory to discuss your matter with our team. Send a message or reach us by phone or email."
        canonicalPath="/contact"
        type="website"
      />
      <PageHero title={heroTitle} imagePath={heroImage} />

      <motion.div
        className="contact-container"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <div className="contact-layout">
          <section className="contact-form-section">
            <h2 className="contact-form-heading">{formHeading}</h2>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-field">
                <input
                  type="text"
                  name="fullName"
                  id="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Full Name"
                  required
                  aria-label="Full Name"
                />
              </div>
              <div className="contact-field">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  required
                  aria-label="Email"
                />
              </div>
              <div className="contact-field">
                <input
                  type="text"
                  name="subject"
                  id="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                  aria-label="Subject"
                />
              </div>
              <div className="contact-field">
                <textarea
                  name="message"
                  id="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message"
                  rows={5}
                  required
                  aria-label="Message"
                />
              </div>
              <button type="submit" className="contact-submit-btn">
                Submit
              </button>
            </form>
          </section>

          <aside className="contact-sidebar">
            <div className="contact-sidebar-block">
              <h3 className="contact-sidebar-title">
                <MapPin size={20} strokeWidth={1.5} className="contact-sidebar-icon" aria-hidden />
                Location
              </h3>
              <p className="contact-sidebar-text">
                {location}
              </p>
            </div>
            <div className="contact-sidebar-block">
              <h3 className="contact-sidebar-title">
                <Mail size={20} strokeWidth={1.5} className="contact-sidebar-icon" aria-hidden />
                Email
              </h3>
              <a href={`mailto:${email}`} className="contact-sidebar-link">
                {email}
              </a>
            </div>
            <div className="contact-sidebar-block">
              <h3 className="contact-sidebar-title">
                <Phone size={20} strokeWidth={1.5} className="contact-sidebar-icon" aria-hidden />
                Phone
              </h3>
              <a href={`tel:${phone}`} className="contact-sidebar-link">
                {phone}
              </a>
            </div>
          </aside>
        </div>
      </motion.div>
    </div>
  );
};

export default ContactPage;
